import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { device } from '../../../constants/device-sizes';
import useKeyPress from '../../../hooks/useKeyPress';
import { getMessageLimit } from '../../../constants/message-constants';
import InputBox from './InputBox';
import SimpleMentionInput from './SimpleMentionInput';
import SubmitButton from './SubmitButton';

const isInStandaloneMode = () =>
  'standalone' in window.navigator && window.navigator.standalone;

export const Container = styled.div`
  padding: 0.75rem 0.5rem 0.75rem 0.5rem;
  padding-bottom: ${isInStandaloneMode() ? '1.75rem' : '0.75rem'};
  width: 100%;
  display: flex;
  flex-direction: column;
  @media ${device.tablet} {
    width: 50%;
  }
  background-color: var(--clr-bg);
`;

const InputRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const InputWrapper = styled.div`
  position: relative;
  flex-grow: 1;
  display: flex;
  align-items: center;
  margin-right: 0.25rem;
`;

const ReplyPreview = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-left: 3px solid ${(props) => props.$replyColor || 'var(--clr-purple)'};
  padding: 0.5rem;
  margin-bottom: 0.5rem;
  border-radius: 0 6px 6px 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  z-index: 100;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
`;

const ReplyContent = styled.div`
  flex: 1;
  min-width: 0; /* Critical for text truncation in flex children */
  display: flex;
  flex-direction: column;
`;

const ReplyHeader = styled.div`
  font-size: 11px;
  color: ${(props) => props.$replyColor || 'var(--clr-purple)'};
  font-weight: 500;
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ReplyText = styled.div`
  font-size: 12px;
  color: var(--clr-sec);
  opacity: 0.8;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--clr-sec);
  opacity: 0.6;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: opacity 0.2s ease, background-color 0.2s ease;
  font-size: 14px;
  min-width: 24px;
  min-height: 24px;
  flex-shrink: 0;
  margin-left: auto; /* Push to the right */

  &:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.1);
  }

  @media (max-width: 768px) {
    padding: 8px;
    min-width: 32px;
    min-height: 32px;
    font-size: 16px;
    opacity: 0.8;
  }
`;

const CharacterCounter = styled.div`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 11px;
  color: var(--clr-sec);
  opacity: 0.6;
  white-space: nowrap;
  pointer-events: none;

  ${props => props.$isNearLimit && `
    opacity: 0.8;
    color: #ff9800;
  `}

  ${props => props.$isOverLimit && `
    opacity: 1;
    color: #ff6b6b;
  `}
`;

function InputContainer(props) {
  const [inputText, setInputText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const inputBox = useRef();
  const enabled = useRef(true);
  
  // Get messages for @mention autocomplete
  const messages = useSelector((state) => Object.values(state.messages.byId));
  
  // Get subscription-aware message limit
  const messageLimit = getMessageLimit(props.hasSubscription);

  useEffect(() => {
    enabled.current = props.enabled;
  }, [props.enabled]);

  // Handle reply from parent component
  useEffect(() => {
    if (props.replyToMessage) {
      setReplyingTo(props.replyToMessage);
      // Focus input when starting a reply
      if (inputBox.current) {
        inputBox.current.focus();
      }
    }
  }, [props.replyToMessage?.id, props.replyToMessage]); // Include id to detect changes even with same object

  function submitMessage() {
    if (enabled.current) {
      console.log('Submitting message:', JSON.stringify(inputText), 'Length:', inputText.length);
      if (inputText && inputText.trim().length > 0) {
        // Pass full reply information if replying
        const messageData = {
          messageText: inputText,
          replyToId: replyingTo?.id || null,
          replyToText: replyingTo?.messageText || null,
          replyToNickname: replyingTo?.nickname || null,
          replyToUserId: replyingTo?.userId || null,
          replyToColor: replyingTo?.color || null,
        };
        props.sendMessage(messageData);
        setInputText('');
        setReplyingTo(null);
      } else {
        console.log('Message is empty, not sending');
      }
    }
  }

  function clearReply() {
    setReplyingTo(null);
  }

  useKeyPress('Enter', () => {
    submitMessage();
  });

  // Use simple mention input that won't interfere with normal typing
  const useMentions = true; // Simple version that shouldn't cause issues
  
  return (
    <Container>
      {/* Reply Preview */}
      {replyingTo && (
        <ReplyPreview $replyColor={replyingTo.color}>
          <ReplyContent>
            <ReplyHeader $replyColor={replyingTo.color}>
              ↳ Replying to {replyingTo.nickname || 'Anon'}
            </ReplyHeader>
            <ReplyText>
              {replyingTo.messageText}
            </ReplyText>
          </ReplyContent>
          <CloseButton onClick={clearReply}>
            ✕
          </CloseButton>
        </ReplyPreview>
      )}

      <InputRow>
        <InputWrapper>
          {useMentions ? (
            <SimpleMentionInput
              ref={inputBox}
              value={inputText}
              messages={messages}
              messageLimit={messageLimit}
              onChange={(text) => {
                setInputText(text);
              }}
              onSubmit={submitMessage}
            />
          ) : (
            <InputBox
              ref={inputBox}
              value={inputText}
              messageLimit={messageLimit}
              onChange={(text) => {
                setInputText(text);
              }}
            />
          )}

          {/* Character Counter - only show when there's text */}
          {inputText.length > 0 && (
            <CharacterCounter
              $isNearLimit={inputText.length > messageLimit * 0.8}
              $isOverLimit={inputText.length > messageLimit}
            >
              {inputText.length}/{messageLimit}
            </CharacterCounter>
          )}
        </InputWrapper>

        <SubmitButton onSubmit={submitMessage} enabled={props.enabled} />
      </InputRow>
    </Container>
  );
}

export default InputContainer;
